import { createContext, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { Dialog, DialogActions } from '../../../design-system/Overlay';
import { Icon } from '../../../design-system/icons';
import { InfoTip } from '../../editor/StudioPanels';
import type { Template } from '../layout-model';

/**
 * Langues de l'éditeur v2 (ADR-146 addendum (14) « Langues », planche « Panneaux-Langues ») : un seul champ dans
 * la langue par défaut ; un seul bouton « Langues » dans l'en-tête de l'inspecteur, avec le nombre de traductions
 * manquantes, ouvre une fenêtre pour tous les textes de l'élément ou du formulaire (une colonne par langue,
 * « à traduire », « suggéré »), « + Langue » et la suggestion automatique. Une suggestion
 * vient de la capacité d'IA du Core, jamais d'un appel du studio à un fournisseur ; elle reste marquée
 * (`translation_suggestions`) jusqu'à « Accepter ». Une langue incomplète ne bloque jamais.
 */

type Localized = Record<string, string>;
type Suggestion = NonNullable<Template['translation_suggestions']>[number];

const LOCALE = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/;
const LOCALIZED_KEYS = new Set(['label', 'description', 'message', 'title', 'content', 'placeholder', 'overline', 'next_label', 'submit_label', 'confirm_label', 'cancel_label', 'in_series_message', 'alt', 'prompt', 'text', 'eyebrow']);

/** Langues proposées par « Ajouter une langue », avec leur nom dans la langue elle-même. */
export const LANGUAGE_NAMES: Readonly<Record<string, { own: string; fr: string }>> = {
  fr: { own: 'Français', fr: 'français' },
  en: { own: 'English', fr: 'anglais' },
  de: { own: 'Deutsch', fr: 'allemand' },
  es: { own: 'Español', fr: 'espagnol' },
  it: { own: 'Italiano', fr: 'italien' },
  nl: { own: 'Nederlands', fr: 'néerlandais' },
  pt: { own: 'Português', fr: 'portugais' },
  pl: { own: 'Polski', fr: 'polonais' },
  ro: { own: 'Română', fr: 'roumain' },
  ar: { own: 'العربية', fr: 'arabe' },
};

export const languageName = (locale: string): string => LANGUAGE_NAMES[locale]?.own ?? locale.toUpperCase();

const isLocalized = (value: unknown): value is Localized => value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0 && Object.entries(value).every(([key, item]) => LOCALE.test(key) && typeof item === 'string');
const escapePointer = (key: string): string => key.replace(/~/g, '~0').replace(/\//g, '~1');

/** Textes localisés d'un gabarit, par pointeur (même lecture que la gate des traductions du Core). */
export function localizedTexts(body: unknown): Array<{ pointer: string; text: Localized }> {
  const out: Array<{ pointer: string; text: Localized }> = [];
  const walk = (node: unknown, pointer: string, parent: string | undefined): void => {
    if (node === null || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(item, `${pointer}/${index}`, parent));
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      const at = `${pointer}/${escapePointer(key)}`;
      if ((LOCALIZED_KEYS.has(key) || parent === 'enum_labels') && isLocalized(value)) out.push({ pointer: at, text: value });
      else if (key !== 'translation_suggestions') walk(value, at, key);
    }
  };
  walk(body, '', undefined);
  return out;
}

/** Langues du formulaire : la langue par défaut, puis celles de ses textes, puis celles ajoutées. */
export function templateLocales(template: Template, defaultLocale: string, added: readonly string[] = []): string[] {
  const found = new Set<string>([defaultLocale]);
  for (const entry of localizedTexts(template)) for (const locale of Object.keys(entry.text)) found.add(locale);
  for (const locale of added) found.add(locale);
  return [defaultLocale, ...[...found].filter((locale) => locale !== defaultLocale).sort()];
}

/** Couverture par langue : textes traduits sur le total (« Anglais 24 sur 28 »). */
export function localeCoverage(template: Template, locales: readonly string[]): Array<{ locale: string; done: number; total: number }> {
  const texts = localizedTexts(template).filter((entry) => Object.values(entry.text).some((value) => value.trim() !== ''));
  return locales.map((locale) => ({ locale, done: texts.filter((entry) => (entry.text[locale] ?? '').trim() !== '').length, total: texts.length }));
}

/** Retire les marques « suggérée » d'un texte (une langue, ou toutes). */
export function clearSuggestions(template: Template, pointer: string, locale?: string): Template {
  const current = template.translation_suggestions ?? [];
  const kept = current.filter((suggestion) => !(suggestion.pointer === pointer && (locale === undefined || suggestion.locale === locale)));
  if (kept.length === current.length) return template;
  const next = { ...template } as Template & Record<string, unknown>;
  if (kept.length === 0) delete next['translation_suggestions'];
  else next.translation_suggestions = kept;
  return next;
}

/** Ce que l'inspecteur fournit aux champs de texte localisé. */
export interface LocaleEnv {
  defaultLocale: string;
  locales: readonly string[];
  /** Ajoute une langue à la liste de travail (elle n'existe dans le gabarit qu'une fois un texte traduit). */
  addLocale: (locale: string) => void;
  suggestions: readonly Suggestion[];
  /** Retire une marque « suggérée » (Accepter, ou texte modifié à la main). */
  accept: (pointer: string, locale: string) => void;
  /**
   * Suggestion automatique par la capacité d'IA du Core (moteur de traduction d'abord, `AIDecision`) ; absente
   * quand elle n'est pas servie dans ce tenant : les boutons sont alors grisés avec leur motif. Rend une valeur
   * par langue suggérée ; la marque « suggérée » suit la valeur si l'auteur la garde.
   */
  suggest?: (source: string, sourceLocale: string, locales: readonly string[], pointer?: string) => Promise<Record<string, string>>;
}

export const NO_SUGGESTION_REASON = 'Suggestion automatique non disponible pour ce tenant.';

/** Suggestion rendue par le serveur : la valeur et sa marque, avec la décision qui l'a produite. */
export interface PendingSuggestion {
  mark: Suggestion;
  text: string;
}

/** Port de la traduction suggérée (SuggestTranslations, puis GetTranslationSuggestions). */
export interface TranslatePort {
  suggest(texts: ReadonlyArray<{ pointer: string; text: string }>, sourceLocale: string, locales: readonly string[]): Promise<{ suggestions: PendingSuggestion[]; missing: ReadonlyArray<{ pointer: string; locale: string }> }>;
}

/**
 * Range la marque des suggestions que l'auteur a gardées telles quelles : une valeur modifiée avant
 * l'enregistrement n'est plus une suggestion. Les marques rangées quittent la liste en attente.
 */
export function keepSuggestionMarks(template: Template, pending: Map<string, PendingSuggestion>): Template {
  if (pending.size === 0) return template;
  const texts = new Map(localizedTexts(template).map((entry) => [entry.pointer, entry.text] as const));
  const kept: Suggestion[] = [];
  for (const [key, entry] of pending) {
    if (texts.get(entry.mark.pointer)?.[entry.mark.locale] !== entry.text) continue;
    kept.push(entry.mark);
    pending.delete(key);
  }
  if (kept.length === 0) return template;
  const others = (template.translation_suggestions ?? []).filter((mark) => !kept.some((entry) => entry.pointer === mark.pointer && entry.locale === mark.locale));
  return { ...template, translation_suggestions: [...others, ...kept] };
}

const LocaleContext = createContext<LocaleEnv>({ defaultLocale: 'fr', locales: ['fr'], addLocale: () => undefined, suggestions: [], accept: () => undefined });

export function LocaleProvider({ env, children }: { env: LocaleEnv; children: ReactNode }) {
  return <LocaleContext.Provider value={env}>{children}</LocaleContext.Provider>;
}

export const useLocales = (): LocaleEnv => useContext(LocaleContext);

const cleaned = (value: Localized): Localized | undefined => {
  const out = Object.fromEntries(Object.entries(value).filter(([, item]) => item.trim() !== ''));
  return Object.keys(out).length === 0 ? undefined : out;
};

/**
 * Texte localisé de l'inspecteur : un seul champ dans la langue par défaut. Plus de compteur ni de « Traduire »
 * par texte : la traduction se fait dans la fenêtre « Langues » de l'en-tête. Une pastille seulement sur un
 * texte qu'il reste à traduire (ou dont une suggestion est à relire).
 */
export function LocalizedText({ label, value, onChange, pointer, required = false, multiline = false, help, placeholder }: {
  label: string;
  value: Localized | undefined;
  onChange: (value: Localized | undefined) => void;
  /** Pointeur du texte dans le gabarit : sert aux marques « suggérée ». */
  pointer?: string;
  required?: boolean;
  multiline?: boolean;
  help?: string;
  placeholder?: string;
}) {
  const env = useLocales();
  const id = useId();
  const current = value ?? {};
  const source = (current[env.defaultLocale] ?? '').trim();
  const missing = source === '' ? 0 : env.locales.filter((locale) => locale !== env.defaultLocale && (current[locale] ?? '').trim() === '').length;
  const pending = pointer === undefined ? 0 : env.suggestions.filter((suggestion) => suggestion.pointer === pointer).length;
  const write = (text: string): void => {
    const next = cleaned({ ...current, [env.defaultLocale]: text });
    onChange(next === undefined && required ? { [env.defaultLocale]: '' } : next);
  };
  const props = { id, value: current[env.defaultLocale] ?? '', placeholder, onChange: (event: { target: { value: string } }) => write(event.target.value) };
  const mark = missing > 0 ? `À traduire : ${missing} langue${missing > 1 ? 's' : ''}` : pending > 0 ? 'Suggestion à relire' : undefined;
  return (
    <div className={`ay-props__row ay-v2-text${multiline ? '' : ' is-inline'}`}>
      <span className="ay-v2-text__head">
        <label htmlFor={id} className="ay-field__label">{label}</label>
        {help !== undefined && <InfoTip label={`Aide : ${label}`}>{help}</InfoTip>}
        <span className="ay-studio__spacer" />
        {mark !== undefined && (
          <span className="ay-v2-text__todo" title={mark} data-testid="text-todo">
            <span className="ay-visually-hidden">{mark}</span>
          </span>
        )}
      </span>
      {multiline ? <textarea rows={2} {...props} /> : <input type="text" {...props} />}
    </div>
  );
}

// ---- Fenêtre « Langues » (planche « Panneaux-Langues ») : tous les textes de l'élément ou du formulaire.

/** Texte traduisible du gabarit, avec ce qu'il est en mots (« Libellé », « Poids saisi · Choix »). */
export interface TextRow {
  pointer: string;
  what: string;
  text: Localized;
}

const KEY_WORDS: Readonly<Record<string, string>> = {
  label: 'Libellé',
  description: 'Aide',
  placeholder: 'Texte indicatif',
  title: 'Titre',
  message: 'Message',
  content: 'Texte',
  overline: 'Surtitre',
  eyebrow: 'Surtitre',
  next_label: 'Bouton',
  submit_label: 'Bouton final',
  confirm_label: 'Confirmer',
  cancel_label: 'Revenir',
  in_series_message: 'Message',
  alt: 'Texte alternatif',
  prompt: 'Question',
  text: 'Texte',
};

const unescapePointer = (segment: string): string => segment.replace(/~1/g, '/').replace(/~0/g, '~');

/** Ce qu'est un texte, d'après sa place dans le gabarit (jamais un identifiant). */
export function textWhat(pointer: string): string {
  const segments = pointer.split('/').slice(1).map(unescapePointer);
  const parent = segments[segments.length - 2];
  if (parent === 'enum_labels') return 'Choix';
  if (parent === 'column_labels') return 'Colonne';
  const last = segments[segments.length - 1] ?? '';
  if (last === 'description' && segments.length === 1) return 'Description';
  return KEY_WORDS[last] ?? 'Texte';
}

/** Nom de l'élément qui porte un texte (champ, partie, bloc, page), pour la portée « Tout le formulaire ». */
function ownerName(template: Template, pointer: string): string | undefined {
  const field = /^\/sections\/(\d+)\/(?:fields|repeat\/children\/fields)\/(\d+)/.exec(pointer);
  const pick = (label: unknown): string | undefined => (label !== null && typeof label === 'object' ? ((label as Localized)['fr'] ?? Object.values(label as Localized)[0]) : undefined);
  if (field !== null) {
    const section = template.sections[Number(field[1])];
    const entry = pointer.includes('/repeat/') ? section?.repeat?.children?.fields?.[Number(field[2])] : section?.fields[Number(field[2])];
    return pick(entry?.field.label);
  }
  const section = /^\/sections\/(\d+)/.exec(pointer);
  if (section !== null) return pick(template.sections[Number(section[1])]?.label);
  const layout = (template as { layout?: { blocks?: Array<{ label?: unknown }>; pages?: Array<{ label?: unknown }> } }).layout;
  const block = /^\/layout\/blocks\/(\d+)/.exec(pointer);
  if (block !== null) return pick(layout?.blocks?.[Number(block[1])]?.label) ?? 'Bloc';
  const page = /^\/layout\/pages\/(\d+)/.exec(pointer);
  if (page !== null) return pick(layout?.pages?.[Number(page[1])]?.label) ?? 'Page';
  if (pointer.startsWith('/layout/header')) return 'Bandeau';
  if (pointer.startsWith('/layout/footer')) return 'Barre du bas';
  return undefined;
}

/**
 * Textes d'une portée : ceux dont le pointeur commence par l'un des préfixes (sans ceux qui commencent par une
 * exclusion), ou tout le formulaire si `prefixes` est absent ; chacun nommé en mots.
 */
export function scopeTexts(template: Template, prefixes?: readonly string[], excluded: readonly string[] = []): TextRow[] {
  const inside = (pointer: string, prefix: string): boolean => pointer === prefix || pointer.startsWith(`${prefix}/`);
  return localizedTexts(template)
    .filter((entry) => Object.values(entry.text).some((value) => value.trim() !== ''))
    .filter((entry) => prefixes === undefined || (prefixes.some((prefix) => inside(entry.pointer, prefix)) && !excluded.some((prefix) => inside(entry.pointer, prefix))))
    .map((entry) => {
      const what = textWhat(entry.pointer);
      const owner = prefixes === undefined ? ownerName(template, entry.pointer) : undefined;
      return { pointer: entry.pointer, what: owner === undefined ? what : `${owner} · ${what}`, text: entry.text };
    });
}

/** Traductions manquantes d'une liste de textes : un texte × une langue autre que la langue par défaut. */
export function missingTranslations(rows: readonly TextRow[], locales: readonly string[], defaultLocale: string): number {
  return rows.reduce((total, row) => total + locales.filter((locale) => locale !== defaultLocale && (row.text[locale] ?? '').trim() === '').length, 0);
}

/** Ce que la fenêtre « Langues » demande au formulaire. */
export interface LanguesPort {
  /** Suggestion par la capacité de traduction du Core ; absente quand elle n'est pas servie dans ce tenant. */
  suggest?: (texts: ReadonlyArray<{ pointer: string; text: string }>, sourceLocale: string, locales: readonly string[]) => Promise<ReadonlyArray<{ pointer: string; locale: string; text: string }>>;
  /** Enregistre les textes changés et retire les marques « suggérée » acceptées ou réécrites. */
  save: (changes: ReadonlyArray<{ pointer: string; text: Localized }>, accepted: ReadonlyArray<{ pointer: string; locale: string }>) => void;
}

export interface LanguesScope {
  /** Nom de l'élément (« Poids saisi ») ; absent : le formulaire. */
  name: string;
  element: readonly TextRow[];
  form: readonly TextRow[];
}

/** Bouton « Langues » de l'en-tête de l'inspecteur, avec le nombre de traductions manquantes. */
export function LanguesButton({ scope, port }: { scope: LanguesScope; port: LanguesPort }) {
  const env = useLocales();
  const [open, setOpen] = useState(false);
  const missing = missingTranslations(scope.element, env.locales, env.defaultLocale);
  return (
    <>
      <button type="button" className="ay-studio-langues" title="Traduire tous les textes" aria-label={missing > 0 ? `Langues, ${missing} à traduire` : 'Langues'} onClick={() => setOpen(true)}>
        <Icon name="translate" size={14} />
        Langues
        {missing > 0 && <span className="ay-studio-langues__count" aria-hidden="true">{missing}</span>}
      </button>
      {open && <LanguesDialog scope={scope} port={port} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Fenêtre « Langues » : portée « Cet élément » ou « Tout le formulaire », une colonne par langue, « à traduire »
 * marqué, une valeur suggérée marquée « suggéré » jusqu'à ce qu'on l'accepte ou la réécrive. Une langue incomplète
 * ne bloque jamais. Principale à droite, Annuler juste à gauche, X en haut.
 */
export function LanguesDialog({ scope, port, onClose }: { scope: LanguesScope; port: LanguesPort; onClose: () => void }) {
  const env = useLocales();
  const [range, setRange] = useState<'element' | 'form'>(scope.element.length > 0 ? 'element' : 'form');
  const [draft, setDraft] = useState<Record<string, Localized>>({});
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const [accepted, setAccepted] = useState<ReadonlySet<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = range === 'element' ? scope.element : scope.form;
  const others = env.locales.filter((locale) => locale !== env.defaultLocale);
  const key = (pointer: string, locale: string): string => `${pointer}|${locale}`;
  const marked = useMemo(() => new Set(env.suggestions.map((suggestion) => key(suggestion.pointer, suggestion.locale))), [env.suggestions]);
  const valueOf = (row: TextRow, locale: string): string => draft[row.pointer]?.[locale] ?? row.text[locale] ?? '';
  const suggested = (row: TextRow, locale: string): boolean => valueOf(row, locale).trim() !== '' && !accepted.has(key(row.pointer, locale)) && (fresh.has(key(row.pointer, locale)) || marked.has(key(row.pointer, locale)));
  const write = (row: TextRow, locale: string, text: string): void => {
    setDraft((current) => ({ ...current, [row.pointer]: { ...row.text, ...(current[row.pointer] ?? {}), [locale]: text } }));
    // Réécrite à la main, la valeur n'est plus une suggestion.
    setFresh((current) => {
      const next = new Set(current);
      next.delete(key(row.pointer, locale));
      return next;
    });
    if (marked.has(key(row.pointer, locale))) setAccepted((current) => new Set([...current, key(row.pointer, locale)]));
  };
  const todo = rows.flatMap((row) => others.filter((locale) => valueOf(row, locale).trim() === '').map((locale) => ({ row, locale })));
  const suggest = async (cells: ReadonlyArray<{ row: TextRow; locale: string }>): Promise<void> => {
    if (port.suggest === undefined || cells.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const byPointer = new Map<string, { row: TextRow; locales: Set<string> }>();
      for (const cell of cells) {
        const found = byPointer.get(cell.row.pointer) ?? { row: cell.row, locales: new Set<string>() };
        found.locales.add(cell.locale);
        byPointer.set(cell.row.pointer, found);
      }
      const locales = [...new Set(cells.map((cell) => cell.locale))];
      const texts = [...byPointer.values()].map(({ row }) => ({ pointer: row.pointer, text: valueOf(row, env.defaultLocale) })).filter((entry) => entry.text.trim() !== '');
      const result = await port.suggest(texts, env.defaultLocale, locales);
      const kept = result.filter((entry) => byPointer.get(entry.pointer)?.locales.has(entry.locale) === true);
      if (kept.length === 0) throw new Error('Aucune suggestion pour ces langues.');
      setDraft((current) => {
        const next = { ...current };
        for (const entry of kept) {
          const row = byPointer.get(entry.pointer)!.row;
          next[entry.pointer] = { ...row.text, ...(next[entry.pointer] ?? {}), [entry.locale]: entry.text };
        }
        return next;
      });
      setFresh((current) => new Set([...current, ...kept.map((entry) => key(entry.pointer, entry.locale))]));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Suggestion impossible.');
    } finally {
      setBusy(false);
    }
  };
  const save = (): void => {
    const changes = Object.entries(draft).map(([pointer, text]) => ({ pointer, text: cleaned(text) ?? text }));
    port.save(changes, [...accepted].map((entry) => {
      const at = entry.lastIndexOf('|');
      return { pointer: entry.slice(0, at), locale: entry.slice(at + 1) };
    }));
    onClose();
  };
  const anySuggested = rows.some((row) => others.some((locale) => suggested(row, locale)));
  const noSuggestion = port.suggest === undefined ? NO_SUGGESTION_REASON : undefined;
  return (
    <Dialog
      open
      size="lg"
      title={`Langues · ${scope.name}`}
      onClose={onClose}
      note={anySuggested ? <span className="ay-v2-langues__legend"><span className="ay-v2-langues__tag">suggéré</span>à relire</span> : undefined}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Enregistrer', onClick: save }} />}
    >
      <div className="ay-v2-langues">
        <div className="ay-v2-langues__bar">
          {scope.element.length > 0 && (
            <span className="ay-studio__seg" role="radiogroup" aria-label="Portée">
              <button type="button" role="radio" aria-checked={range === 'element'} onClick={() => setRange('element')}>Cet élément</button>
              <button type="button" role="radio" aria-checked={range === 'form'} onClick={() => setRange('form')}>Tout le formulaire</button>
            </span>
          )}
          <span className="ay-studio__spacer" />
          {adding ? (
            <AddLanguage existing={env.locales} onAdd={(locale) => { env.addLocale(locale); setAdding(false); }} onCancel={() => setAdding(false)} />
          ) : (
            <button type="button" className="ay-button ay-button--secondary" onClick={() => setAdding(true)}>+ Langue</button>
          )}
          <button type="button" className="ay-button ay-button--secondary" disabled={noSuggestion !== undefined || busy || todo.length === 0} title={noSuggestion ?? 'Suggérer les traductions manquantes'} onClick={() => void suggest(todo)}>
            <Icon name="sparkle" size={14} /> Traduire les manquants
          </button>
        </div>
        {error !== null && <p className="ay-studio__refusal" role="alert">{error}</p>}
        {rows.length === 0 ? (
          <p className="ay-studio-empty">Aucun texte.</p>
        ) : (
          <div className="ay-v2-langues__scroll">
            <table className="ay-v2-langues__table">
              <thead>
                <tr>
                  <th scope="col">Texte</th>
                  <th scope="col">{languageName(env.defaultLocale)} · défaut</th>
                  {others.map((locale) => <th key={locale} scope="col">{languageName(locale)}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.pointer}>
                    <th scope="row">{row.what}</th>
                    <td className="is-reference" title={valueOf(row, env.defaultLocale)}><span className="ay-v2-langues__ref">{valueOf(row, env.defaultLocale)}</span></td>
                    {others.map((locale) => {
                      const value = valueOf(row, locale);
                      const state = value.trim() === '' ? 'todo' : suggested(row, locale) ? 'suggested' : 'done';
                      return (
                        <td key={locale} className={`is-${state}`}>
                          <span className="ay-v2-langues__cell">
                            <input type="text" aria-label={`${row.what} en ${languageName(locale)}`} value={value} placeholder="à traduire" onChange={(event) => write(row, locale, event.target.value)} />
                            {state === 'suggested' && (
                              <button type="button" className="ay-v2-langues__tag" title="Accepter la suggestion" aria-label={`Accepter la suggestion : ${row.what} en ${languageName(locale)}`} onClick={() => { setAccepted((current) => new Set([...current, key(row.pointer, locale)])); setFresh((current) => { const next = new Set(current); next.delete(key(row.pointer, locale)); return next; }); }}>
                                suggéré
                              </button>
                            )}
                            {state === 'todo' && noSuggestion === undefined && (
                              <button type="button" className="ay-studio__icon ay-studio__icon--sm" title="Suggérer" aria-label={`Suggérer : ${row.what} en ${languageName(locale)}`} disabled={busy} onClick={() => void suggest([{ row, locale }])}>
                                <Icon name="sparkle" size={12} />
                              </button>
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function AddLanguage({ existing, onAdd, onCancel }: { existing: readonly string[]; onAdd: (locale: string) => void; onCancel: () => void }) {
  const id = useId();
  const options = Object.keys(LANGUAGE_NAMES).filter((locale) => !existing.includes(locale));
  const [choice, setChoice] = useState(options[0] ?? '');
  if (options.length === 0) return <p className="ay-studio-empty">Toutes les langues proposées sont déjà là.</p>;
  return (
    <span className="ay-v2-inline">
      <label htmlFor={id} className="ay-visually-hidden">Langue à ajouter</label>
      <select id={id} value={choice} onChange={(event) => setChoice(event.target.value)}>
        {options.map((locale) => <option key={locale} value={locale}>{languageName(locale)}</option>)}
      </select>
      <button type="button" className="ay-button ay-button--secondary" onClick={() => onAdd(choice)}>Ajouter</button>
      <button type="button" className="ay-v2-link" onClick={onCancel}>Annuler</button>
    </span>
  );
}
